import React, { useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { workspacesApi } from "../../actions";
import { WORKSPACE_ROLES } from "../../utils/permissions";
import { Hint, Refusal, dialogPaper } from "./WorkspaceUi";
import { seatText } from "./workspaceFormat";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Invite by email as Admin or Member, and give them views as they join. A
// view carries the one database it is for, so choosing views here is
// choosing which databases the person reaches.
// A refusal is shown as the server words it; for "seats" we add who can help.
const seatHint = (isStaff) => (isStaff ? " Raise the seats on the Plan tab." : " Ask bpExperts for more seats.");

export default function InviteDialog({ open, workspace, views = [], seats, isStaff = false, onClose, onInvited }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const active = views.filter((view) => !view.archivedAt && !view.builtIn);

  const reset = () => {
    setEmail("");
    setRole("member");
    setChosen([]);
    setError("");
  };
  const close = () => {
    reset();
    onClose();
  };

  const send = async (event) => {
    event.preventDefault();
    if (!EMAIL.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError("");
    const res = await workspacesApi.invite(workspace.id, { email: email.trim(), role, viewIds: chosen.map((view) => view.id) });
    setBusy(false);
    if (!res.success) {
      const msg = res.msg || "Couldn't send the invitation.";
      setError(res.reason === "seats" ? `${msg}${seatHint(isStaff)}` : msg);
      return;
    }
    reset();
    onInvited(res.msg || `Invitation sent to ${email.trim()}.`);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="sm" PaperProps={dialogPaper}>
      <form onSubmit={send} noValidate>
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Invite to {workspace?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            They get an email with a link that works once, for 14 days. A pending invitation holds a seat.
            {seats ? ` ${seatText(seats)}.` : ""}
          </Typography>
          <Stack spacing={2.5}>
            <TextField
              label="Email"
              type="email"
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError("");
              }}
              placeholder="name@company.com"
              autoComplete="off"
            />
            <div>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Role
              </Typography>
              <ToggleButtonGroup exclusive fullWidth value={role} onChange={(e, next) => next && setRole(next)} aria-label="Role">
                {WORKSPACE_ROLES.map((option) => (
                  <ToggleButton
                    key={option.key}
                    value={option.key}
                    sx={{
                      flexDirection: "column",
                      alignItems: "flex-start",
                      textAlign: "left",
                      textTransform: "none",
                      py: 1.25,
                      px: 1.5,
                      "&.Mui-selected": { bgcolor: "rgba(26, 26, 46, 0.06)", borderColor: "primary.main" },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "none" }}>
                      {option.description}
                    </Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </div>
            {role === "admin" && (
              <Hint>Admins use every database of the workspace, so they need no view to reach one.</Hint>
            )}
            {role === "member" && (
              <Autocomplete
                multiple
                options={active}
                value={chosen}
                onChange={(e, next) => setChosen(next)}
                getOptionLabel={(view) => (view.databaseLabel ? `${view.name} · ${view.databaseLabel}` : view.name)}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderTags={(value, getTagProps) => value.map((view, index) => <Chip size="small" label={view.name} {...getTagProps({ index })} />)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Views"
                    placeholder={active.length ? "Choose views" : "No views yet"}
                    helperText={
                      active.length
                        ? "Each view gives its own database, seen through that view. Without one they reach nothing."
                        : "While the workspace has no views, everyone in it views every database."
                    }
                  />
                )}
                disabled={!active.length}
              />
            )}
            {error && <Refusal>{error}</Refusal>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={busy} disabled={!email.trim()}>
            Send invitation
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
